import { InboxOutlined } from '@ant-design/icons';
import type { SagaImportPreview, SagaPreviewRow, SagaRowStatus } from '@financiar/shared';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Modal, Spin, Table, Tag, Tooltip, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, MoneyText } from '../../lib/format';
import { commitSagaImport, previewSagaImport } from './api';

const { Dragger } = Upload;

type ImportPhase = 'upload' | 'preview' | 'importing';

const STATUS_TAG: Record<SagaRowStatus, { color: string; label: string }> = {
  new: { color: 'green', label: 'Nouă' },
  duplicate: { color: 'default', label: 'Duplicat' },
  error: { color: 'red', label: 'Eroare' },
};

/** "57 facturi noi. Se creează: firma DEVWEB SRL, seria DW, 8 clienți noi." —
 * the "Se creează" clause only lists what will ACTUALLY be created, so a
 * re-import of an already-imported file (nothing left to create) reads as
 * just "0 facturi noi." with no dangling clause. */
function summaryMessage(preview: SagaImportPreview): string {
  const parts: string[] = [];
  if (preview.supplier.willCreate) parts.push(`firma ${preview.supplier.name || preview.supplier.cif}`);
  if (preview.series.willCreate) parts.push(`seria ${preview.series.prefix}`);
  if (preview.newClients.length > 0) {
    parts.push(`${preview.newClients.length} client${preview.newClients.length === 1 ? '' : 'i'} noi`);
  }

  const newCount = preview.summary.new;
  const base = `${newCount} factur${newCount === 1 ? 'ă nouă' : 'i noi'}`;
  return parts.length > 0 ? `${base}. Se creează: ${parts.join(', ')}.` : `${base}.`;
}

/**
 * "Importă din Saga" modal (`FacturiListPage`'s toolbar) — two real phases,
 * same shape as `ClientImportModal`: upload a file and get a preview back
 * (writes nothing server-side), then review + commit it. Unlike the client
 * import, there is no `firmaId` prop here at all — the issuing firm is
 * detected (and, on commit, created if missing) from the XML's own
 * `FurnizorCIF`, entirely server-side, so this modal never needs to know
 * which firm is currently selected.
 */
export function SagaImportModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [preview, setPreview] = useState<SagaImportPreview | null>(null);
  const [file, setFile] = useState<File | null>(null);

  function resetToUpload() {
    setPhase('upload');
    setPreview(null);
    setFile(null);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  const previewMutation = useMutation({
    mutationFn: (uploaded: File) => previewSagaImport(uploaded),
    onSuccess: (data, uploaded) => {
      setPreview(data);
      setFile(uploaded);
      setPhase('preview');
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Fișierul nu a putut fi analizat.');
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Fișierul lipsește.');
      return commitSagaImport(file);
    },
    onMutate: () => setPhase('importing'),
    onSuccess: (result) => {
      const parts = [`${result.invoicesImported} factur${result.invoicesImported === 1 ? 'ă importată' : 'i importate'}`];
      if (result.clientsCreated > 0) {
        parts.push(`${result.clientsCreated} client${result.clientsCreated === 1 ? '' : 'i'} creat${result.clientsCreated === 1 ? '' : 'i'}`);
      }
      if (result.skipped > 0) parts.push(`${result.skipped} sărit${result.skipped === 1 ? 'ă' : 'e'} (deja importate)`);
      if (result.failed > 0) parts.push(`${result.failed} eșuat${result.failed === 1 ? 'ă' : 'e'}`);
      message.success(`${parts.join(', ')}.`);
      onDone();
      handleClose();
    },
    onError: (error) => {
      setPhase('preview');
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Importul a eșuat.');
    },
  });

  const columns: ColumnsType<SagaPreviewRow> = [
    { title: 'Număr', dataIndex: 'fullNumber', width: 100, render: (value: string) => value || '—' },
    { title: 'Dată', key: 'issueDate', width: 100, render: (_, row) => <DateText value={row.issueDate || null} /> },
    {
      title: 'Client',
      key: 'client',
      ellipsis: true,
      render: (_, row) => (row.clientName || row.clientCif ? `${row.clientName}${row.clientCif ? ` (${row.clientCif})` : ''}` : '—'),
    },
    {
      title: 'Total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (_, row) => (row.status === 'error' || !row.total ? '—' : <MoneyText value={row.total} />),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, row) => {
        const { color, label } = STATUS_TAG[row.status];
        const tag = <Tag color={color}>{label}</Tag>;
        return row.errors.length > 0 ? <Tooltip title={row.errors.join('; ')}>{tag}</Tooltip> : tag;
      },
    },
  ];

  return (
    <Modal
      title="Importă facturi istorice din Saga"
      open={open}
      onCancel={handleClose}
      width={880}
      destroyOnHidden
      footer={
        phase === 'upload'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Anulează
              </Button>,
            ]
          : [
              <Button key="back" disabled={phase === 'importing'} onClick={resetToUpload}>
                Înapoi
              </Button>,
              <Button key="cancel" disabled={phase === 'importing'} onClick={handleClose}>
                Anulează
              </Button>,
              <Button
                key="import"
                type="primary"
                disabled={!preview || preview.summary.new === 0}
                loading={commitMutation.isPending}
                onClick={() => commitMutation.mutate()}
              >
                Importă {preview?.summary.new ?? 0} facturi
              </Button>,
            ]
      }
    >
      {phase === 'upload' ? (
        <Spin spinning={previewMutation.isPending} tip="Se analizează facturile și se verifică datele la ANAF…">
          <Dragger
            accept=".xml"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(uploaded: RcFile) => {
              previewMutation.mutate(uploaded);
              return false;
            }}
            style={{ padding: 24 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Trage fișierul aici sau apasă pentru a-l selecta</p>
            <p className="ant-upload-hint">
              Export XML din Saga (&lt;Facturi&gt;) — facturi deja emise, importate cu numerele lor originale.
            </p>
          </Dragger>
        </Spin>
      ) : (
        <Spin spinning={phase === 'importing'} tip="Se importă…">
          {preview && (
            <>
              <Alert
                style={{ marginBottom: 16 }}
                type={preview.summary.error > 0 ? 'warning' : 'info'}
                showIcon
                message={summaryMessage(preview)}
                description={
                  preview.summary.duplicate > 0 || preview.summary.error > 0
                    ? `${preview.summary.duplicate} deja importate (se sar), ${preview.summary.error} cu erori.`
                    : undefined
                }
              />
              <Table<SagaPreviewRow>
                rowKey={(row, index) => `${row.fullNumber}-${index}`}
                size="small"
                columns={columns}
                dataSource={preview.rows}
                pagination={false}
                scroll={{ y: 420 }}
              />
            </>
          )}
        </Spin>
      )}
    </Modal>
  );
}
